import * as z from "zod";
import {
  VariantAttributeValueSchema,
  createVariantAttributeValueSchema,
} from "./attributes";

// Base enums
export const ProductStatusEnum = z.enum(["draft", "active"]);
export const RockBoardTypeEnum = z.enum(["custom", "modular"]);
export const EditorTypeEnum = z.enum(["standard", "custom"]);

// PDF Attachment schema
export const PdfAttachmentSchema = z.object({
  name: z.string().min(1, "PDF name is required"),
  url: z.string().url("Valid URL is required"),
});

// Product relationship schemas
export const ProductBrandSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string(),
});

export const ProductVehicleTypeSchema = z.object({
  id: z.string().uuid().optional(),
  vehicle_type_id: z.string().uuid(),
});

export const ProductAttachmentSchema = z.object({
  id: z.string().uuid().optional(),
  attachment_id: z.string().uuid(),
  product_id: z.string().uuid(),
  quantity: z.number().int().positive().default(1),
});

export const ProductExclusiveCompanySchema = z.object({
  id: z.string().uuid().optional(),
  company_id: z.string().uuid(),
});

export const ProductTagSchema = z.object({
  tag_id: z.string().uuid(),
  name: z.string().optional(),
});

export const CompanyProductPartNumberSchema = z.object({
  id: z.string().uuid().optional(),
  company_id: z.string().uuid(),
  company_name: z.string().optional(),
  product_id: z.string().uuid().optional(),
  part_number: z
    .string()
    .min(1, "Part number is required")
    .max(100, "Part number must be less than 100 characters"),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

export const ProductPricingTierSchema = z.object({
  id: z.string().uuid().optional(),
  tier_id: z.string().uuid(),
  product_id: z.string().uuid().optional(),
  base_price: z
    .number()
    .positive("Base price must be a positive number")
    .multipleOf(0.01),
  compare_price: z
    .number()
    .positive("Compare price must be a positive number")
    .multipleOf(0.01),
  profit: z.string(),
  margin: z.string(),
  name: z.string(),
});

export const ProductRockBoardSchema = z.object({
  id: z.string().uuid(),
  type: RockBoardTypeEnum.default("custom"),
  voltage: z.number().positive().multipleOf(0.01).nullable(),
  current: z.number().positive().multipleOf(0.01).nullable(),
});

export const ProductCustomSchema = z.object({
  id: z.string().uuid().optional(),
  design_id: z.string().uuid().optional(),
  min_chars: z.number().int().positive().nullable().optional(),
  max_chars: z.number().int().positive().nullable().optional(),
  char_height: z.number().min(0).multipleOf(0.001).nullable().optional(),
  char_width: z.number().min(0).multipleOf(0.001).nullable().optional(),
  bound_width: z.number().min(0).multipleOf(0.001).nullable().optional(),
  bound_height: z.number().min(0).multipleOf(0.001).nullable().optional(),
  overlay_image: z.string().nullable().optional(),
  overlay_x: z.number().int().nullable().optional(),
  overlay_y: z.number().int().nullable().optional(),
  overlay_width: z.number().int().nullable().optional(),
  overlay_height: z.number().int().nullable().optional(),
  background_image: z.string().nullable().optional(),
  background_x: z.number().int().nullable().optional(),
  background_y: z.number().int().nullable().optional(),
  background_width: z.number().int().nullable().optional(),
  background_height: z.number().int().nullable().optional(),
  inset_width: z.number().int().positive().nullable().optional(),
  inset_height: z.number().int().positive().nullable().optional(),
  inset_x: z.number().int().nullable().optional(),
  inset_y: z.number().int().nullable().optional(),
});

export const ProductVariantSchema = z.object({
  id: z.string().uuid(),
  product_id: z.string().uuid(),
  parent_id: z.string().uuid(),
});

// Base product schema
const baseProductSchema = z.object({
  id: z.string().uuid(),
  title: z
    .string()
    .min(1, "Title is required")
    .max(255, "Title must be less than 255 characters"),
  part_number: z
    .string()
    .min(1, "Part number is required")
    .max(100, "Part number must be less than 100 characters"),
  moq: z.number().int().positive("MOQ must be a positive number"),
  description: z.string().min(1, "Description is required"),
  features: z.array(z.string()).optional().default([]),
  images: z.array(z.string()).optional().default([]),
  hover_image: z.string().nullable().optional(),
  pdf_attachments: z.array(PdfAttachmentSchema).optional().default([]),
  cost_price: z
    .number()
    .positive("Cost price must be a positive number")
    .multipleOf(0.01),
  tax_rate: z.number().min(0).max(100).multipleOf(0.01),
  weight: z
    .number()
    .positive("Weight must be a positive number")
    .multipleOf(0.001),
  width: z
    .number()
    .positive("Width must be a positive number")
    .multipleOf(0.001)
    .optional(),
  height: z
    .number()
    .positive("Height must be a positive number")
    .multipleOf(0.001)
    .optional(),
  length: z
    .number()
    .positive("Length must be a positive number")
    .multipleOf(0.001)
    .optional(),
  shipping_width: z
    .number()
    .positive("Shipping width must be a positive number")
    .multipleOf(0.001)
    .optional(),
  shipping_height: z
    .number()
    .positive("Shipping height must be a positive number")
    .multipleOf(0.001)
    .optional(),
  shipping_length: z
    .number()
    .positive("Shipping length must be a positive number")
    .multipleOf(0.001)
    .optional(),
  shipping_weight: z
    .number()
    .positive("Shipping weight must be a positive number")
    .multipleOf(0.001),
  meta_title: z.string().max(255).optional().nullable(),
  meta_description: z.string().optional().nullable(),
  editor: EditorTypeEnum.optional(),
  brand_id: z.string().uuid(),
  status: ProductStatusEnum.default("draft"),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
  // Relationships
  product_brand: ProductBrandSchema.optional(),
  vehicle_types: z.array(ProductVehicleTypeSchema).optional().default([]),
  product_pricing_tiers: z
    .array(ProductPricingTierSchema)
    .optional()
    .default([]),
  attachments: z.array(ProductAttachmentSchema).optional().default([]),
  exclusive_companies: z
    .array(ProductExclusiveCompanySchema)
    .optional()
    .default([]),
  company_part_numbers: z
    .array(CompanyProductPartNumberSchema)
    .optional()
    .default([]),
  rock_board: ProductRockBoardSchema.optional(),
  custom: ProductCustomSchema.optional().nullable(),
  tags: z.array(ProductTagSchema).optional().default([]),
  attribute_values: z.array(VariantAttributeValueSchema).optional().default([]),
  product_groups: z
    .array(
      z.object({
        id: z.string(),
        name: z.string().optional(),
        // All other fields optional
        description: z.string().nullable().optional(),
        slug: z.string().optional(),
        hero_image: z.string().nullable().optional(),
        images: z.array(z.string()).nullable().optional(),
        created: z.date().optional(),
        updated: z.date().optional(),
        product_ids: z.array(z.string()).optional(),
        brand_ids: z.array(z.string()).optional(),
      })
    )
    .optional()
    .default([]),
  parent: ProductVariantSchema.nullable().optional(),
});

export const ProductSchema = baseProductSchema.refine(
  (data) => {
    // Only validate custom fields when editor is "custom"
    if (data.editor === "custom") {
      return !!data.custom;
    }
    return true;
  },
  {
    message: "Custom configuration is required when editor is set to custom",
    path: ["custom"],
  }
);

// Create and update schemas
export const createProductSchema = baseProductSchema
  .omit({
    id: true,
    created: true,
    updated: true,
  })
  .extend({
    attribute_values: z.array(createVariantAttributeValueSchema).optional(),
  });

export const updateProductSchema = baseProductSchema
  .partial()
  .omit({
    id: true,
    created: true,
    updated: true,
  })
  .extend({
    attribute_values: z.array(createVariantAttributeValueSchema).optional(),
  });

// Stripe product schema
export const stripeProductSchema = z.object({
  id: z.string().optional(),
  object: z.string().default("product"),
  active: z.boolean().default(true),
  created: z.number().default(Date.now()),
  default_price: z.string().nullable(),
  default_price_data: z
    .object({
      currency: z.string(),
      currency_options: z.record(z.any()).optional(),
      custom_unit_amount: z.object({}).optional(),
      recurring: z.object({}).optional(),
      tax_behavior: z
        .enum(["exclusive", "inclusive", "unspecified"])
        .optional()
        .default("exclusive"),
      unit_amount: z.number().int().positive().or(z.literal(0)).optional(),
      unit_amount_decimal: z.string().optional(),
      metadata: z
        .record(z.string().max(500))
        .refine(
          (metadata) =>
            Object.keys(metadata).length <= 50 &&
            Object.keys(metadata).every((key) => key.length <= 40),
          {
            message:
              "Metadata can have up to 50 keys, with key names up to 40 characters and values up to 500 characters",
          }
        )
        .optional(),
    })
    .optional(),
  description: z.string().optional(),
  images: z.array(z.string()).max(8).optional(),
  marketing_features: z
    .array(z.object({ name: z.string().max(80) }))
    .max(15)
    .optional(),
  livemode: z.boolean().default(false),
  metadata: z
    .record(z.string().max(500))
    .refine(
      (metadata) =>
        Object.keys(metadata).length <= 50 &&
        Object.keys(metadata).every((key) => key.length <= 40),
      {
        message:
          "Metadata can have up to 50 keys, with key names up to 40 characters and values up to 500 characters",
      }
    )
    .optional(),
  name: z.string(),
  package_dimensions: z
    .object({
      height: z.number().positive().multipleOf(0.01),
      length: z.number().positive().multipleOf(0.01),
      weight: z.number().positive().multipleOf(0.01),
      width: z.number().positive().multipleOf(0.01),
    })
    .optional()
    .nullable(),
  shippable: z.boolean().default(true),
  tax_code: z.string().optional().nullable(),
  unit_label: z.string().optional().nullable(),
  updated: z.number().default(Date.now()),
  url: z.string().optional().nullable(),
});

export type StripeProduct = z.infer<typeof stripeProductSchema>;
